import {
  useGetAiAgentsSettingService,
  useUpdateAiAgentServicesSetting,
} from '@/context/services/ai_agents_setting/AiAgentsSetting.service';
import { UpdateAiAgentServicesSettingResult } from '@/context/services/ai_agents_setting/type';
import { handlePermissionError } from '@/context/services/ai_agents_setting/utils';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { UpdateAiAgentServiceSettingInputServiceType } from 'ap-openapi';
import { useCallback, useState } from 'react';

type ServiceFormData = {
  invoice_download_agent: boolean;
  payment_request_support_agent: boolean;
};

type UseUpdateServicesSettingReturn = {
  updateServicesSetting: (
    data: ServiceFormData,
    successCallback?: () => void
  ) => Promise<void>;
  isUpdating: boolean;
};

export const useUpdateServicesSetting = (
  isPaymentRequestSupportAgentEnabled = false
): UseUpdateServicesSettingReturn => {
  const [isUpdating, setIsUpdating] = useState(false);
  const notify = useToastAPI();
  const { mutateAsync } = useUpdateAiAgentServicesSetting();
  const { refetch } = useGetAiAgentsSettingService();
  const { t } = useTranslation();
  const updateServicesSetting = useCallback(
    async (data: ServiceFormData, successCallback?: () => void) => {
      setIsUpdating(true);
      try {
        // Transform form data to API format
        const apiData = [
          {
            service_type:
              UpdateAiAgentServiceSettingInputServiceType.invoice_download_agent,
            is_enabled: data.invoice_download_agent,
          },
          // Only send the payment request support agent while the feature flag
          // is enabled, so the service stays hidden in environments where it is
          // not yet released.
          ...(isPaymentRequestSupportAgentEnabled
            ? [
                {
                  service_type:
                    UpdateAiAgentServiceSettingInputServiceType.payment_request_support_agent,
                  is_enabled: data.payment_request_support_agent,
                },
              ]
            : []),
        ];
        const resp: UpdateAiAgentServicesSettingResult = await mutateAsync(
          apiData
        );

        if (resp.status === 'success') {
          notify.success({
            title: t('ai_agents_setting_services_update_success'),
            duration: 5,
          });
          successCallback?.();
        } else {
          // Handle errors: refetch first, reload only if refetch also fails with 401/403
          const shouldReload = await handlePermissionError(
            resp.statusCode,
            refetch
          );
          if (shouldReload) {
            return;
          }
          const errorMessage = resp.errorMessages?.length
            ? resp.errorMessages.join(', ')
            : t('ai_agents_setting_services_update_error');
          notify.error({
            title: errorMessage,
            duration: 5,
          });
        }
      } catch (error) {
        // Show error toast
        notify.error({
          title: t('ai_agents_setting_services_update_error'),
          duration: 5,
        });
        throw error;
      } finally {
        setIsUpdating(false);
      }
    },
    [mutateAsync, refetch, notify, t, isPaymentRequestSupportAgentEnabled]
  );

  return {
    updateServicesSetting,
    isUpdating,
  };
};
